import axios from 'axios'

// NO default Content-Type here — axios v1 sets application/json for plain-object bodies
// by itself. Forcing it globally broke every FormData upload: axios v1's transformRequest
// sees FormData + a JSON content-type and CONVERTS the FormData to JSON
// (JSON.stringify(formDataToJSON(data))), which keeps the text fields but silently
// discards the file bytes. That was why escalation/e-payment attachments posted as
// text-only with uploads=0 and no error anywhere (found 2026-07-24).
const api = axios.create({
  baseURL: import.meta.env.DEV ? 'http://localhost:3003' : import.meta.env.VITE_API_URL,
  withCredentials: true,
})

// Token getter - will be set by auth composable
let getAuthToken = null

export function setAuthTokenGetter(getter) {
  getAuthToken = getter
}

// The most recent token a request actually went out with. For the one caller that
// cannot await the async getter — a keepalive fetch fired from `pagehide` to flush
// an undo-window SMS before the tab closes (useDelayedSend). Clerk tokens live ~60s,
// so this is fresh enough for a beacon and never for anything else.
let lastToken = null
export function peekAuthToken() {
  return lastToken
}
export function apiBaseURL() {
  return api.defaults.baseURL || ''
}

// Add request interceptor to include auth token.
// The token fetch is bounded: a stalled Clerk refresh must never hang the request
// forever (that surfaced as a reminder Snooze button that "just spins"). If the
// token isn't back in time we send without it — the backend answers 401, which the
// caller can handle, instead of the request never settling. axios's own `timeout`
// only starts after interceptors run, so it can't catch a hang here.
const TOKEN_FETCH_TIMEOUT_MS = 8000
api.interceptors.request.use(
  async (config) => {
    if (getAuthToken) {
      try {
        const token = await Promise.race([
          getAuthToken(),
          new Promise((resolve) => setTimeout(() => resolve(null), TOKEN_FETCH_TIMEOUT_MS)),
        ])
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
          lastToken = token
        }
      } catch (error) {
        console.warn('Failed to get auth token:', error)
      }
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.error('Unauthorized access')
    }
    return Promise.reject(error)
  }
)

export default api
